import { Injectable, signal } from '@angular/core';
import { Observable, defer, delay, of } from 'rxjs';
import type { EmployeeVarianceManagerClient, IncidentsQueryContext } from '../../../../../core/tokens/employee-variance-manager.token';
import { TableQuery } from '../../../../../shared/components/generic-table/models/table-query.model';
import type { CaseInvestigation, CreateCaseInvestigation } from '../../../case-investigation/models/case-investigation.models';
import { TableDataResponse } from '../../../services/variance-tracker-mock.service';
import { EmployeeVarianceManagementRow } from '../models/employee-variance-management-row.model';

@Injectable({
  providedIn: 'root',
})
export class EmployeeVarianceManagementMockService implements EmployeeVarianceManagerClient {
  private readonly varianceManagementRows: readonly EmployeeVarianceManagementRow[] =
    this.generateVarianceManagementRows(2);

  readonly options = signal<{ label: string; value: string }[]>([
    { label: 'Attention Req!', value: 'Attention Required' },
    { label: 'Monitor', value: 'Monitor' },
    { label: 'No Longer Employed', value: 'No Longer Employed' },
    { label: 'Practice intervention', value: 'Practice intervention' },
    { label: 'Referral', value: 'Referral' },
    { label: 'Requires Review', value: 'Requires Review' },
    { label: 'Rev In Progress', value: 'Rev In Progress' },
    { label: 'Suspended', value: 'Suspended' },
    { label: 'Terminated', value: 'Terminated' },
    { label: 'Unassigned', value: 'Unassigned' },
    { label: 'Under Invest', value: 'Under Investigation' },
  ]);

  getVarianceManagementRows(
    query: TableQuery,
    _context?: IncidentsQueryContext
  ): Observable<TableDataResponse<EmployeeVarianceManagementRow>> {
    return defer(() => {
      const normalized = this.normalizeQuery(query);
      const afterSearch = this.applySearch(this.varianceManagementRows, normalized.search);
      const afterColumnFilters = this.applyColumnFilters(afterSearch, normalized.filters);
      const sorted = this.applySort(afterColumnFilters, normalized.sortField, normalized.sortOrder);
      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);
      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<EmployeeVarianceManagementRow>);
    }).pipe(delay(250));
  }

  private applyColumnFilters(
    rows: readonly EmployeeVarianceManagementRow[],
    filters: Record<string, string | string[]> | undefined
  ): EmployeeVarianceManagementRow[] {
    if (!filters || Object.keys(filters).length === 0) return [...rows];
    return rows.filter((row) => {
      return Object.entries(filters).every(([field, filterValue]) => {
        const cell = row[field];
        const cellStr = cell != null ? String(cell).toLowerCase() : '';
        if (Array.isArray(filterValue)) {
          const terms = filterValue.map((v) => v.trim().toLowerCase()).filter(Boolean);
          return terms.length === 0 || terms.some((t) => cellStr.includes(t));
        }
        const term = (filterValue ?? '').trim().toLowerCase();
        return !term || cellStr.includes(term);
      });
    }) as EmployeeVarianceManagementRow[];
  }

  private applySearch(rows: readonly EmployeeVarianceManagementRow[], search: string | undefined) {
    const term = (search ?? '').trim().toLowerCase();
    if (!term) return [...rows];

    return rows.filter(
      (r) =>
        r.varianceStatus.toLowerCase().includes(term) ||
        r['Medication'].toLowerCase().includes(term) ||
        r.Patient.toLowerCase().includes(term) ||
        r['Patient ID'].toLowerCase().includes(term) ||
        r.Date.toLowerCase().includes(term) ||
        r.Time.toLowerCase().includes(term) ||
        r['Clinical Unit'].toLowerCase().includes(term) ||
        r.Shift.toLowerCase().includes(term)
    ) as EmployeeVarianceManagementRow[];
  }
  private applySort(
    rows: EmployeeVarianceManagementRow[],
    sortField: string | undefined,
    sortOrder: 1 | -1 | undefined
  ): EmployeeVarianceManagementRow[] {
    if (!sortField) return rows;
    const order: 1 | -1 = sortOrder === -1 ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * order;
      if (bv == null) return 1 * order;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;
      return String(av).localeCompare(String(bv)) * order;
    }) as EmployeeVarianceManagementRow[];
  }

  normalizeQuery(q: TableQuery): Required<Pick<TableQuery, 'page' | 'rows'>> & TableQuery {
    return {
      ...q,
      page: Math.max(0, q.page ?? 0),
      rows: Math.max(1, q.rows ?? 10),
    };
  }

  generateVarianceManagementRows(count: number): EmployeeVarianceManagementRow[] {
    const varianceStatuses: readonly string[] = [
      'Pending Response',
      'Open'
    ];
    const incidentNumbers: readonly number[] = Array.from({ length: count }, (_, i) => 10024 + i);
    const medications: readonly string[] = ['Oxycodone Tab', 'Hydromorphone'];
    const varAmts: readonly number[] = Array.from({ length: count }, (_, i) => 5 + i);
    const uoms: readonly string[] = ['mg', 'g', 'ml', 'units', 'mcg', 'mcg/ml', 'mcg/g', 'mcg/l'];
    const patients: readonly string[] = [
      'Mia Anderson',
      'Emma Johnson',
      'Liam Smith',
      'Olivia Brown',
      'Noah Jones',
      'Ava Williams',
      'Isabella Miller',
      'Sophia Garcia',
      'Mia Rodriguez',
      'Charlotte Martinez',
    ];
    const patientIds: readonly string[] = [
      'NMZX1928',
      'NMZX1929',
      'NMZX1930',
      'NMZX1931',
      'NMZX1932',
      'NMZX1933',
      'NMZX1934',
      'NMZX1935',
      'NMZX1936',
      'NMZX1937',
    ];
    const dates: readonly string[] = [
      '02/04/2024',
      '02/05/2024',
      '02/06/2024',
      '02/07/2024',
      '02/08/2024',
      '02/09/2024',
      '02/10/2024',
      '02/11/2024',
      '02/12/2024',
      '02/13/2024',
    ];
    const times: readonly string[] = [
      '05:30pm',
      '05:31pm',
      '05:32pm',
      '05:33pm',
      '05:34pm',
      '05:35pm',
      '05:36pm',
      '05:37pm',
      '05:38pm',
      '05:39pm',
    ];
    const clinicalUnits: readonly string[] = ['H-Green', 'G-Neuro/Surg'];
    const shifts: readonly string[] = [
      '1st',
      '2nd',
      '3rd',
      '4th',
      '5th',
      '6th',
      '7th',
      '8th',
      '9th',
      '10th',
    ];
    const normalizedMedIds: readonly number[] = [1, 2];
    const locationIds: readonly number[] = [1, 2];
    const shiftIds: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    return Array.from({ length: count }, (_, i) => ({
      varianceStatus: varianceStatuses[i % varianceStatuses.length],
      'Incident #': incidentNumbers[i % incidentNumbers.length],
      Medication: medications[i % medications.length],
      'Var Amt': varAmts[i % varAmts.length],
      UOM: uoms[i % uoms.length],
      Patient: patients[i % patients.length],
      'Patient ID': patientIds[i % patientIds.length],
      Date: dates[i % dates.length],
      Time: times[i % times.length],
      'Clinical Unit': clinicalUnits[i % clinicalUnits.length],
      Shift: shifts[i % shifts.length],
      NormalizedMedId: normalizedMedIds[i % normalizedMedIds.length],
      LocationId: locationIds[i % locationIds.length],
      ShiftId: shiftIds[i % shiftIds.length],
      Guid: `guid-${Date.now()}-${i}`,
    }));
  }

  createCaseInvestigation(caseInvestigation: CreateCaseInvestigation): Observable<CaseInvestigation> {
    const mockCaseInvestigation: CaseInvestigation = {
      id: Math.floor(Math.random() * 1000) + 1,
      guid: `guid-${Date.now()}`,
      userId: 1, // mock internal id; request uses employeeId (string)
      dateOfEvent: caseInvestigation.dateOfEvent ?? new Date().toISOString(),
      auditorId: caseInvestigation.auditorId ?? 1,
      attachments: caseInvestigation.attachments ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: null,
      closedAt: null,
      deletedAt: null,
    };
    return of(mockCaseInvestigation).pipe(delay(300));
  }

  createCaseInvestigations(caseInvestigations: CreateCaseInvestigation[]): Observable<CaseInvestigation[]> {
    const mockCaseInvestigations: CaseInvestigation[] = caseInvestigations.map((caseInvestigation, index) => ({
      id: Math.floor(Math.random() * 1000) + index + 1,
      guid: `guid-${Date.now()}-${index}`,
      userId: 1,
      dateOfEvent: caseInvestigation.dateOfEvent ?? new Date().toISOString(),
      auditorId: caseInvestigation.auditorId ?? 1,
      locationId: caseInvestigation.locationId ?? 1,
      shiftId: caseInvestigation.shiftId ?? 1,
      attachments: caseInvestigation.attachments ?? null,
      createdAt: new Date().toISOString(),
      updatedAt: null,
      closedAt: null,
      deletedAt: null,
    }));
    return of(mockCaseInvestigations).pipe(delay(2000));
  }
}
